import React, { useCallback, useEffect, useState } from 'react';

import {
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  InputAdornment,
  TextField,
} from '@mui/material';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import { AccountIndexSchema, AccountStatus, PrefTypeOptions } from '@liscio/api';
import { Box, Typography } from '@liscio/ui';

import { AccountSuggestionPanel } from './AccountSuggestionPanel';
import { CreateAccountAdditionalDetailsSection } from './CreateAccountAdditionalDetailsSection';
import {
  LabeledField,
} from './CreateAccountFormFieldPrimitives';
import { CreateAccountFormValues } from './createAccountValidationSchema';
import { filterCreateAccountStatusOptions } from './filterCreateAccountStatusOptions';
import { InlineRichOptionSelect } from './InlineRichOptionSelect';
import { CreateAccountFormLayout } from './types';
import {
  isMeaningfulAccountNameQuery,
  useAccountSuggestionsByName,
} from './useAccountSuggestionsByName';
import { useCreateAccountEntityTypeReset } from './useCreateAccountEntityTypeReset';
import { useHomeData , useProfileData } from 'fetch-utils/users/user-hooks';

export type { CreateAccountFormLayout } from './types';

export interface CreateAccountFormFieldsProps {
  /**
   * `responsive` — Entity Type and Status sit side-by-side from `sm` breakpoint up.
   * `single-column` — all fields full width (narrow panels, modals, drawers).
   */
  layout?: CreateAccountFormLayout;
  /** Override dropdown options; defaults to `useHomeData().pref_type_options`. */
  prefTypeOptions?: Pick<
    PrefTypeOptions,
    | 'entity_type'
    | 'account_status'
    | 'account_stage'
    | 'referral_source'
    | 'account_rating'
    | 'industry'
    | 'filing_status'
    | 'dependent_relationship'
  >;
  /** When true, renders the collapsible optional-fields accordion. */
  enableAdditionalDetails?: boolean;
  /** When true, searches for existing accounts by name and shows dedupe suggestions. */
  enableNameSuggestions?: boolean;
  onSelectExistingAccount?: (account: AccountIndexSchema) => void;
  onClearExistingAccountSelection?: () => void;
  /** Hide suggestions after an existing account has been selected. */
  hideNameSuggestions?: boolean;
  /** When false, hides the Restricted Account checkbox (e.g. onboarding). */
  showRestrictedAccount?: boolean;
}

/**
 * Account creation fields (name, entity type, status, stage).
 * Must be rendered inside a parent `FormProvider` — use `useCreateAccountForm()`.
 * Fills 100% of parent width; no max-width — container controls size.
 */
export const CreateAccountFormFields: React.FC<CreateAccountFormFieldsProps> = ({
  layout = 'responsive',
  prefTypeOptions: prefTypeOptionsProp,
  enableAdditionalDetails = false,
  enableNameSuggestions = false,
  onSelectExistingAccount,
  onClearExistingAccountSelection,
  hideNameSuggestions = false,
  showRestrictedAccount = true,
}) => {
  const { data: homeData } = useHomeData();
  const { isFirmAdmin } = useProfileData();
  const {
    register,
    control,
    setValue,
    formState: { errors },
  } = useFormContext<CreateAccountFormValues>();
  const [isNameFocused, setIsNameFocused] = useState(false);

  useCreateAccountEntityTypeReset();

  const prefTypeOptions =
    prefTypeOptionsProp ?? homeData?.data?.pref_type_options;

  const entityTypeOptions = prefTypeOptions?.entity_type;
  const accountStatusOptions = filterCreateAccountStatusOptions(
    prefTypeOptions?.account_status
  );
  const accountStageOptions = prefTypeOptions?.account_stage;

  const accountName = useWatch<CreateAccountFormValues, 'name'>({ name: 'name' });
  const trimmedAccountName = (accountName ?? '').trim();
  const hasMeaningfulNameQuery = isMeaningfulAccountNameQuery(trimmedAccountName);
  const status = useWatch<CreateAccountFormValues, 'status'>({ name: 'status' });
  const isClientStatus =
    status?.toLowerCase() === AccountStatus.Client.toLowerCase();

  // Stage only applies to non-client (prospect) accounts. When the status
  // becomes Client, clear any previously-chosen stage so a stale value isn't
  // retained or submitted (the field is also hidden below).
  useEffect(() => {
    if (isClientStatus) {
      setValue('stage', '');
    }
  }, [isClientStatus, setValue]);
  const { suggestions, isLoading: isLoadingSuggestions } =
    useAccountSuggestionsByName(accountName ?? '', {
      enabled: enableNameSuggestions,
    });

  const handleSelectExistingAccount = useCallback(
    (account: AccountIndexSchema) => {
      onSelectExistingAccount?.(account);
      setIsNameFocused(false);
    },
    [onSelectExistingAccount]
  );

  const showNameSuggestions =
    enableNameSuggestions &&
    !hideNameSuggestions &&
    hasMeaningfulNameQuery &&
    (isNameFocused || isLoadingSuggestions || suggestions.length > 0);

  const pairedFieldBreakpoint = layout === 'single-column' ? 12 : 6;

  return (
    <Grid container spacing={2.5} width="100%" sx={{ m: 0 }}>
      <Grid item xs={12}>
        <Box position="relative" width="100%">
          <LabeledField label="Account Name" required>
            <Controller
              control={control}
              name="name"
              render={({ field }) => (
                <TextField
                  {...field}
                  value={field.value ?? ''}
                  fullWidth
                  placeholder="Acme Holdings LLC"
                  error={Boolean(errors.name)}
                  helperText={errors.name?.message}
                  autoComplete="off"
                  onFocus={() => setIsNameFocused(true)}
                  onBlur={() => {
                    window.setTimeout(() => setIsNameFocused(false), 150);
                  }}
                  onChange={(event) => {
                    field.onChange(event);
                    onClearExistingAccountSelection?.();
                  }}
                  InputProps={{
                    endAdornment:
                      enableNameSuggestions && isLoadingSuggestions ? (
                        <InputAdornment position="end">
                          <CircularProgress color="inherit" size={16} />
                        </InputAdornment>
                      ) : undefined,
                  }}
                />
              )}
            />
          </LabeledField>

          {!hideNameSuggestions &&
            hasMeaningfulNameQuery &&
            suggestions.length > 0 &&
            !isLoadingSuggestions && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Select an existing account below or edit the name to create a new
                one.
              </Typography>
            )}

          {showNameSuggestions && (
            <Box
              sx={{
                position: 'relative',
                zIndex: (theme) => theme.zIndex.modal + 1,
              }}
            >
              <AccountSuggestionPanel
                nameQuery={trimmedAccountName}
                suggestions={suggestions}
                isLoading={isLoadingSuggestions}
                onSelectExisting={handleSelectExistingAccount}
              />
            </Box>
          )}
        </Box>
      </Grid>

      {entityTypeOptions && !hideNameSuggestions && (
        <Grid item xs={12} sm={pairedFieldBreakpoint}>
          <LabeledField label="Entity Type" required>
            <InlineRichOptionSelect
              name="entity_type"
              control={control}
              data={entityTypeOptions}
              error={errors.entity_type}
            />
          </LabeledField>
        </Grid>
      )}

      {accountStatusOptions && !hideNameSuggestions && (
        <Grid item xs={12} sm={pairedFieldBreakpoint}>
          <LabeledField label="Status" required>
            <InlineRichOptionSelect
              name="status"
              control={control}
              data={accountStatusOptions}
              error={errors.status}
            />
          </LabeledField>
        </Grid>
      )}

      {accountStageOptions && !hideNameSuggestions && !isClientStatus && (
        <Grid item xs={12}>
          <LabeledField label="Prospect Stage" required>
            <InlineRichOptionSelect
              name="stage"
              control={control}
              data={accountStageOptions}
              error={errors.stage}
            />
          </LabeledField>
        </Grid>
      )}

      {showRestrictedAccount && !hideNameSuggestions && (
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                {...register('is_restricted_account')}
                disabled={!isFirmAdmin}
              />
            }
            label="Restricted Account?"
            sx={{ mb: 0 }}
          />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', ml: 4, mt: -0.25 }}
          >
            Restricts visibility to Firm Admins and the assigned Client Service
            Team.
          </Typography>
        </Grid>
      )}

      {enableAdditionalDetails && (
        <Grid item xs={12}>
          <CreateAccountAdditionalDetailsSection
            layout={layout}
            prefTypeOptions={prefTypeOptions}
          />
        </Grid>
      )}
    </Grid>
  );
};

export default CreateAccountFormFields;
